<template>
  <div>
    <member-selector
      class="no-print"
      :members="members"
      :groups="groups"
      :selected-member.sync="selectedMember"
      :selected-group.sync="selectedGroups"
      :selected-job-level.sync="selectedJobLevel"
    />
    <v-row class="no-print pa-2" align="center">
      <v-col cols="auto">
        <v-checkbox
          v-for="s in statusOptions"
          :key="s.value"
          :value="s.value"
          :input-value="selectedStatuses"
          hide-details
          class="d-inline-flex mr-4"
          @change="toggleStatus(s.value)"
        >
          <template v-slot:label>
            <v-icon
              small
              :color="s.color"
              class="mr-1"
            >
              {{ STATUS_ICONS[s.value] }}
            </v-icon>
            {{ s.label }}
          </template>
        </v-checkbox>
      </v-col>
      <v-spacer />
      <v-col cols="auto" class="d-flex align-center">
        <v-switch
          v-model="groupByGroup"
          hide-details
          dense
          class="mr-4"
          :label="'Group by ' + groupLabel"
        />
        <v-switch
          v-model="showDetails"
          hide-details
          dense
          class="mr-4"
          label="Details"
        />
        <v-btn
          color="primary"
          large
          @click="exportExcel"
          :loading="exporting"
        >
          <v-icon class="mr-2">mdi-file-excel</v-icon>
          Export to Excel
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="filteredData.length" class="no-print pa-2">
      <v-col cols="auto" v-for="s in statusOptions" :key="'sum-'+s.value">
        <v-chip
          small
          outlined
          :color="s.color"
          v-if="selectedStatuses.includes(s.value) && summaryByStatus[s.value]"
        >
          <v-icon x-small class="mr-1">{{ statusIcon(s.value) }}</v-icon>
          {{ summaryByStatus[s.value] }}
        </v-chip>
      </v-col>
    </v-row>
    <v-data-table
      :group-by="groupByGroup ? 'group' : ''"
      sort-by="totalOt"
      sort-desc
      :headers="headers"
      :items-per-page="filteredData.length"
      dense
      item-key="memberId"
      :expanded="showDetails ? (expanded || filteredData) : []"
      :items="filteredData"
    >
      <template v-slot:item.sn="{ index }">
        <span>{{ index + 1 }}</span>
      </template>

      <template v-slot:item.totalOt="{ item }">
        <div v-if="selectedStatuses.length > 1">
          <div v-for="s in selectedStatuses" :key="s" class="d-flex align-center" v-if="item.totalOtByStatus && item.totalOtByStatus[s]">
            <v-icon
              small
              :color="s === 'Approved' ? 'green' : s === 'Ready' ? 'blue' : 'grey'"
              class="mr-1"
            >
              {{ statusIcon(s) }}
            </v-icon>
            <v-chip
              x-small
              :color="otChipColor('afterWork')"
              text-color="white"
              class="mr-1"
            >
              {{ item.totalOtByStatus[s] | number('0.00') }}
            </v-chip>
          </div>
        </div>
        <v-chip
          v-else
          x-small
          :color="otChipColor('afterWork')"
          text-color="white"
        >
          {{ item.totalOt | number('0.00') }}
        </v-chip>
      </template>

      <template v-slot:item.otAfterWork="{ item }">
        <div>
          <div v-for="s in selectedStatuses" :key="s" class="d-flex align-center" v-if="item.ot && item.ot[s] && item.ot[s].afterWork">
            <v-icon
              small
              :color="s === 'Approved' ? 'green' : s === 'Ready' ? 'blue' : 'grey'"
              class="mr-1"
            >
              {{ statusIcon(s) }}
            </v-icon>
            <v-chip
              x-small
              :color="otChipColor('afterWork')"
              text-color="white"
            >
              {{ item.ot[s].afterWork | number('0.00') }}
            </v-chip>
          </div>
        </div>
      </template>

      <template v-slot:item.otNight="{ item }">
        <div>
          <div v-for="s in selectedStatuses" :key="s" class="d-flex align-center" v-if="item.ot && item.ot[s] && item.ot[s].night">
            <v-icon
              small
              :color="s === 'Approved' ? 'green' : s === 'Ready' ? 'blue' : 'grey'"
              class="mr-1"
            >
              {{ statusIcon(s) }}
            </v-icon>
            <v-chip
              x-small
              :color="otChipColor('night')"
              text-color="white"
            >
              {{ item.ot[s].night | number('0.00') }}
            </v-chip>
          </div>
        </div>
      </template>

      <template v-slot:item.otRestDay="{ item }">
        <div>
          <div v-for="s in selectedStatuses" :key="s" class="d-flex align-center" v-if="item.ot && item.ot[s] && item.ot[s].restDay">
            <v-icon
              small
              :color="s === 'Approved' ? 'green' : s === 'Ready' ? 'blue' : 'grey'"
              class="mr-1"
            >
              {{ statusIcon(s) }}
            </v-icon>
            <v-chip
              x-small
              :color="otChipColor('restDay')"
              text-color="white"
            >
              {{ item.ot[s].restDay | number('0.00') }}
            </v-chip>
          </div>
        </div>
      </template>

      <template v-slot:item.otHollyDay="{ item }">
        <div>
          <div v-for="s in selectedStatuses" :key="s" class="d-flex align-center" v-if="item.ot && item.ot[s] && item.ot[s].hollyDay">
            <v-icon
              small
              :color="s === 'Approved' ? 'green' : s === 'Ready' ? 'blue' : 'grey'"
              class="mr-1"
            >
              {{ statusIcon(s) }}
            </v-icon>
            <v-chip
              x-small
              :color="otChipColor('hollyDay')"
              text-color="white"
            >
              {{ item.ot[s].hollyDay | number('0.00') }}
            </v-chip>
          </div>
        </div>
      </template>

      <template v-slot:item.action="{ item }">
        <v-icon
          medium
          @click="$router.push({params:{id:item.memberId}})"
        >
          mdi-text-box
        </v-icon>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="2" />

        <td>
          <div v-for="a of sortedOtRecords(item.otRecords)" :key="a.date + '-afterWork'">
            <div v-if="a.ot && a.ot.afterWork">
              <v-icon
                small
                :color="statusIconColor(a.status)"
                class="mr-1"
              >
                {{ statusIcon(a.status) }}
              </v-icon>
              {{ a.date | moment('DD ddd') }}
              <ot-display
                label=""
                :remark="a.remark"
                :ot="a.ot.afterWork"
              />
            </div>
          </div>
        </td>

        <td>
          <div v-for="a of sortedOtRecords(item.otRecords)" :key="a.date + '-night'">
            <div v-if="a.ot && a.ot.night">
              <v-icon
                small
                :color="statusIconColor(a.status)"
                class="mr-1"
              >
                {{ statusIcon(a.status) }}
              </v-icon>
              {{ a.date | moment('DD ddd') }}
              <ot-display
                label=""
                :remark="a.remark"
                :ot="a.ot.night"
              />
            </div>
          </div>
        </td>

        <td>
          <div v-for="a of sortedOtRecords(item.otRecords)" :key="a.date + '-restDay'">
            <div v-if="a.ot && a.ot.restDay">
              <v-icon
                small
                :color="statusIconColor(a.status)"
                class="mr-1"
              >
                {{ statusIcon(a.status) }}
              </v-icon>
              {{ a.date | moment('DD ddd') }}
              <ot-display
                label=""
                :remark="a.remark"
                :ot="a.ot.restDay"
              />
            </div>
          </div>
        </td>

        <td>
          <div v-for="a of sortedOtRecords(item.otRecords)" :key="a.date + '-hollyDay'">
            <div v-if="a.ot && a.ot.hollyDay">
              <v-icon
                small
                :color="statusIconColor(a.status)"
                class="mr-1"
              >
                {{ statusIcon(a.status) }}
              </v-icon>
              {{ a.date | moment('DD ddd') }}
              <ot-display
                label=""
                :remark="a.remark"
                :ot="a.ot.hollyDay"
              />
            </div>
          </div>
        </td>

        <td />
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import { fetchOTReport } from '@/helpers/tikApi'
  import { getOtColors } from '@/helpers/DynamicColors'
  import MemberSelector from '@/views/dashboard/components/MemberSelector'
  import OtDisplay from '@/views/dashboard/components/OtDisplay'
  import moment from 'moment'
  import * as XLSX from 'xlsx'

  const STATUS_OPTIONS = [
    { value: 'Approved', label: 'Approved', color: 'green' },
    { value: 'Ready', label: 'Ready', color: 'blue' },
    { value: 'Tentative', label: 'Tentative', color: 'grey' },
  ]

  const STATUS_ICONS = {
    Approved: 'mdi-check-all',
    Ready: 'mdi-book-arrow-up',
    Tentative: 'mdi-timer-sand-full',
  }

  export default {
    name: 'DetailTime',
    components: { OtDisplay, MemberSelector },
    data: () => ({
      expanded: null,
      headers: [
        { name: 'sn', value: 'sn', width: '50px' },
        { text: 'Name', value: 'name', width: '250px' },
        { text: 'Normal Day', value: 'otAfterWork', width: '100px' },
        { text: 'Night', value: 'otNight', width: '100px' },
        { text: 'Rest Day', value: 'otRestDay', width: '100px' },
        { text: 'Holly Day', value: 'otHollyDay', width: '100px' },
        { text: 'Total', value: 'totalOt', width: '120px' },
      ],
      groupByGroup: true,
      selectedMember: [],
      selectedGroups: [],
      selectedJobLevel: [],
      loading: false,
      reportData: [],
      selectedStatuses: ['Approved'],
      statusOptions: STATUS_OPTIONS,
      STATUS_ICONS,
      showDetails: true,
      exporting: false,
    }),
    computed: {
      ...mapState('records', ['startDate', 'endDate']),
      ...mapGetters(['team', 'groups', 'groupLabel', 'parentGroupLabel', 'groupLabelPlural', 'parentGroupLabelPlural']),
      members () {
        return this.reportData.map(m => ({ fullName: m.name, id: m.memberId, group: m.group }))
      },
      filteredData () {
        return this.reportData.filter(m =>
          (!this.selectedMember.length || this.selectedMember.includes(m.memberId)) &&
          (!this.selectedGroups.length || this.selectedGroups.includes(m.group)) &&
          (!this.selectedJobLevel.length || (m.jobLevel && this.selectedJobLevel.includes(m.jobLevel)))
        )
      },
      summaryByStatus () {
        const total = {}
        this.filteredData.forEach(m => {
          if (m.totalOtByStatus) {
            Object.keys(m.totalOtByStatus).forEach(s => {
              total[s] = (total[s] || 0) + m.totalOtByStatus[s]
            })
          }
        })
        Object.keys(total).forEach(k => { total[k] = total[k].toFixed(2) })
        return total
      },
    },
    watch: {
      startDate: 'loadReport',
      endDate: 'loadReport',
      selectedStatuses: 'loadReport',
    },
    methods: {
      statusIcon (status) {
        return STATUS_ICONS[status] || STATUS_ICONS.Tentative
      },
      statusIconColor (status) {
        if (status === 'Approved') return 'green'
        if (status === 'Ready') return 'blue'
        return 'grey'
      },
      sortedOtRecords (records) {
        if (!records) return []
        return [...records].sort((a, b) => new Date(a.date) - new Date(b.date))
      },
      otChipColor (field) {
        return getOtColors(field)
      },
      toggleStatus (value) {
        const idx = this.selectedStatuses.indexOf(value)
        if (idx >= 0) {
          if (this.selectedStatuses.length > 1) {
            this.selectedStatuses.splice(idx, 1)
          }
        } else {
          this.selectedStatuses.push(value)
        }
      },
      exportExcel () {
        this.exporting = true
        const sDate = moment(this.startDate).format('MMMM D, YYYY')
        const eDate = moment(this.endDate).format('MMMM D, YYYY')
        const title = `Overtime Detail Report`
        const dateRange = `Period: ${sDate} - ${eDate}`
        const companyHeader = `${this.$store.getters.company || ''} ${this.$store.getters.teamFull?.name || ''}`.trim()
        const fmt = (val) => val ? parseFloat(parseFloat(val).toFixed(2)) : 0

        const otFields = ['afterWork', 'night', 'restDay', 'hollyDay']
        const otLabels = ['Normal Day', 'Night', 'Rest Day', 'Holly Day']
        const statuses = this.selectedStatuses
        const withDetails = this.showDetails

        const columns = statuses.length > 1
          ? [this.groupLabel, 'Name', ...otFields.flatMap((f, fi) => statuses.map(s => `${otLabels[fi]} (${s})`)), ...statuses.map(s => `Total (${s})`)]
          : [this.groupLabel, 'Name', ...otLabels, 'Total']

        const toRow = (m) => {
          const row = []
          row.push(m.group || '')
          row.push(m.name)
          if (statuses.length > 1) {
            otFields.forEach(fld => {
              statuses.forEach(s => {
                row.push(fmt(m.ot && m.ot[s] ? m.ot[s][fld] : 0))
              })
            })
            statuses.forEach(s => {
              row.push(fmt(m.totalOtByStatus && m.totalOtByStatus[s] ? m.totalOtByStatus[s] : 0))
            })
          } else {
            otFields.forEach(fld => {
              let sum = 0
              Object.values(m.ot || {}).forEach(so => { if (so && so[fld]) sum += so[fld] })
              row.push(fmt(sum))
            })
            row.push(fmt(m.totalOt))
          }
          return row
        }

        const buildHeader = () => {
          const rows = []
          if (companyHeader) rows.push([companyHeader])
          rows.push([title])
          rows.push([dateRange, ...new Array(columns.length - 1).fill('')])
          rows.push([])
          return rows
        }

        const data = [...buildHeader(), columns]

        this.filteredData.forEach(m => {
          data.push(toRow(m))
          if (withDetails && m.otRecords && m.otRecords.length) {
            const sorted = [...m.otRecords].sort((a, b) => new Date(a.date) - new Date(b.date))
            sorted.forEach((r, ri) => {
              const prefix = ri === 0 ? '  ' : ''
              const dateLabel = `${moment(r.date).format('DD, ddd')}`
              otFields.forEach((fld, fi) => {
                const val = r.ot && r.ot[fld] ? r.ot[fld] : 0
                if (val > 0) {
                  const hrs = Math.floor(val)
                  const mins = Math.round((val - hrs) * 60)
                  const timeStr = mins > 0 ? `${hrs}h${mins}m` : `${hrs}h`
                  const remark = r.remark || r.status || 'Tentative'
                  const row = new Array(columns.length).fill('')
                  row[0] = prefix
                  if (statuses.length > 1) {
                    statuses.forEach((s, si) => {
                      const colIdx = 2 + fi * statuses.length + si
                      if (s === (r.status || 'Tentative') || (r.status === null && s === 'Tentative')) {
                        row[colIdx] = `${dateLabel} ${timeStr} (${remark})`
                      }
                    })
                  } else {
                    row[2 + fi] = `${dateLabel} ${timeStr} (${remark})`
                  }
                  data.push(row)
                }
              })
            })
          }
        })

        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.aoa_to_sheet(data)
        const colWidths = columns.map((c, i) => {
          if (i < 2) return { wch: 30 }
          return { wch: 14 }
        })
        ws['!cols'] = colWidths
        XLSX.utils.book_append_sheet(wb, ws, 'OT Report')
        XLSX.writeFile(wb, `OT Report - ${sDate} - ${eDate}.xlsx`)
        this.exporting = false
      },
      async loadReport () {
        if (!this.startDate || !this.endDate || !this.selectedStatuses.length) return
        this.loading = true
        try {
          this.reportData = await fetchOTReport(
            this.startDate,
            this.endDate,
            this.team,
            false,
            null,
            this.selectedStatuses
          )
        } catch (e) {
          console.error('Failed to load OT report', e)
        } finally {
          this.loading = false
        }
      },
      expandAll () {
        this.expanded = this.filteredData
      },
      collapseAll () {
        this.expanded = []
      },
    },
    created () {
      this.loadReport()
    },
  }
</script>

<style scoped>

</style>